/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: formDefaults.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Default values for Appearance form.

 🔁 Last Updated: 2025-01-XX by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import { getInputBackgroundColor } from '../../../store/theme';
import type { LightModeVariant, DarkModeVariant } from '../../../store/theme';
import type { AppearanceForm } from '../types';
import { DEFAULT_WIDGET_DARK, DEFAULT_WIDGET_LIGHT } from '../types';

/**
 * Get default appearance form values
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getDefaultAppearanceValues(themePrefs: any): AppearanceForm {
  return {
    mode: themePrefs.mode || 'dark',
    darkModeVariant: themePrefs.darkModeVariant || 'navy',
    lightModeVariant: themePrefs.lightModeVariant || 'white',
    primaryColor: themePrefs.primaryColor || '#3B82F6',
    secondaryColor: themePrefs.secondaryColor || '#64748B',
    tooltips: {
      lightTheme: {
        backgroundColor: themePrefs.tooltips?.lightTheme?.backgroundColor || getInputBackgroundColor('light', themePrefs.lightModeVariant || 'white'),
        textColor: themePrefs.tooltips?.lightTheme?.textColor || '#1E293B',
        borderRadius: themePrefs.tooltips?.lightTheme?.borderRadius || 0.5
      },
      darkTheme: {
        backgroundColor: themePrefs.tooltips?.darkTheme?.backgroundColor || getInputBackgroundColor('dark', undefined, themePrefs.darkModeVariant || 'very-dark-gray'),
        textColor: themePrefs.tooltips?.darkTheme?.textColor || '#F1F5F9',
        borderRadius: themePrefs.tooltips?.darkTheme?.borderRadius || 0.5
      }
    },
    notifications: {
      lightTheme: {
        successColor: themePrefs.notifications?.lightTheme?.successColor || '#22c55e',
        errorColor: themePrefs.notifications?.lightTheme?.errorColor || '#ef4444',
        warningColor: themePrefs.notifications?.lightTheme?.warningColor || '#f59e0b',
        infoColor: themePrefs.notifications?.lightTheme?.infoColor || '#3b82f6'
      },
      darkTheme: {
        successColor: themePrefs.notifications?.darkTheme?.successColor || '#22c55e',
        errorColor: themePrefs.notifications?.darkTheme?.errorColor || '#ef4444',
        warningColor: themePrefs.notifications?.darkTheme?.warningColor || '#f59e0b',
        infoColor: themePrefs.notifications?.darkTheme?.infoColor || '#3b82f6'
      }
    },
    // Dashboard Widget Settings
    widget: themePrefs.widget || DEFAULT_WIDGET_DARK,
    widgetDark: themePrefs.widgetDark || DEFAULT_WIDGET_DARK,
    widgetLight: themePrefs.widgetLight || DEFAULT_WIDGET_LIGHT,
    gridRowHeight: themePrefs.gridRowHeight || 10,
    gridColumns: themePrefs.gridColumns || 48,
    // Global border radius
    borderRadius: themePrefs.borderRadius ?? 0.5
  };
}

/**
 * Get reset appearance form values
 */
export function getResetAppearanceValues(lightModeVariant: string, darkModeVariant: string): AppearanceForm {
  return {
    mode: 'dark',
    darkModeVariant: 'navy' as any,
    lightModeVariant: 'white' as any,
    primaryColor: '#3B82F6',
    secondaryColor: '#64748B',
    tooltips: {
      lightTheme: {
        backgroundColor: getInputBackgroundColor('light', (lightModeVariant || 'white') as LightModeVariant),
        textColor: '#1E293B',
        borderRadius: 0.5
      },
      darkTheme: {
        backgroundColor: getInputBackgroundColor('dark', undefined, (darkModeVariant || 'very-dark-gray') as DarkModeVariant),
        textColor: '#F1F5F9',
        borderRadius: 0.5
      }
    },
    notifications: {
      lightTheme: {
        successColor: '#22c55e',
        errorColor: '#ef4444',
        warningColor: '#f59e0b',
        infoColor: '#3b82f6'
      },
      darkTheme: {
        successColor: '#22c55e',
        errorColor: '#ef4444',
        warningColor: '#f59e0b',
        infoColor: '#3b82f6'
      }
    },
    // Dashboard Widget Settings - Reset to defaults
    widget: DEFAULT_WIDGET_DARK,
    widgetDark: DEFAULT_WIDGET_DARK,
    widgetLight: DEFAULT_WIDGET_LIGHT,
    gridRowHeight: 10,
    gridColumns: 48,
    borderRadius: 0.5
  };
}

